import { useLazyQuery, useMutation } from '@apollo/client/react';
import { Button, Form, Input, message, Modal, Upload } from 'antd';
import { filter, includes, map, trim } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../../../../../AppContext';
import { WrongIcon } from '../../../../../../assets/svg';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  ALLOWED_IMAGE_TYPES,
  DATETIMEWITHDIVIDE,
  DEFAULTDATEFORMAT,
  ELEMENT_IDS,
  FALLBACK_PDF_URL,
  FIELD_TYPES,
  FILE_SIZE_8MB,
  INVALID_FIELD_VALUES,
  PROJECT_ROLES,
  TIME_24_HOUR_FORMAT,
  USER_ROLES,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import {
  addWatermark,
  calculateFieldValue,
  compressImage,
  dataURLtoFile,
  fileUpload,
  formatNumericInput,
} from '../../../../../../common/utils';
import CanPerform from '../../../../../../components/CanPerform';
import { GET_URLS } from '../../../../../approver/graphql/Queries';
import {
  CREATE_REGISTER_TABLE_FIELDS,
  UPDATE_REGISTER_LOG_DETAIL_ENTRY,
} from '../../../../graphql/Mutation';
import FieldIcon from './FieldIcon';
import RenderFields from './RenderFields';
import TableFieldUnsaveChangeModal from './TableFieldUnsaveChangeModal';

const AddEditTableFieldModal = ({
  tableForm,
  tableHeaders,
  setTableData,
  fieldsData,
  rowRecord,
  setRowRecord,
  fieldIndex,
  setFieldIndex,
  showModal,
  setShowModal,
  fileListRef,
  isFieldClicked,
  handleTableRouterPromptUpdate,
  handleFieldsDataRefUpdate,
  handleFileListRefUpdate,
  setEditingKeys,
  handleRegisterRefetch,
  handleRegisterLogDetailRefetch,
  handleHisotryRefetch,
  onTableDirty,
  onTableErrorCheck,
  modifiedFieldsRef,
  handleSaveAsDraft,
  showTableFieldConfirmModal,
  setShowTableFieldConfirmModal,
}) => {
  const {
    params: { registerLogId },
  } = useRouter();
  const { dispatch, getCurrentUserRole, getCurrentProjectDetail, state } =
    useContext(AppContext);
  const currentProjectDetails = getCurrentProjectDetail();
  const [form] = Form.useForm();
  const [isFormChanged, setIsFormChanged] = useState(false);
  const fileLists = useRef({});
  const [initialKeys, setInitialKeys] = useState({});
  const [isOptionsFocused, setIsOptionsFocused] = useState(null);
  const [isMultiSelectOptionsFocused, setIsMultiSelectOptionsFocused] =
    useState(null);
  const [isDateFocused, setIsDateFocused] = useState(null);
  const [isTimeFocused, setIsTimeFocused] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddMoreDisabled, setIsAddMoreDisabled] = useState(true);
  const saveClickedRef = useRef(false);
  const tempUploadDataRef = useRef(null);
  const imageKeys = useRef([]);
  const isFocusedFieldRef = useRef(null);
  const addMoreButtonRef = useRef(null);
  const isAuditor =
    getCurrentUserRole() === USER_ROLES.EXTERNAL_AUDITOR ||
    state?.projectRole === PROJECT_ROLES.AUDITOR;

  const showRouterPrompt = (value) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: value,
    });
  };

  const ensureEmptyRowExists = () => {
    const currentData = fieldsData?.current || [];

    const dataWithoutEmptyRows = currentData?.filter((row) => {
      if (!row?.isEmptyRow) return true;

      const hasData = tableHeaders?.some((header) => {
        const value = row[header?.dataIndex];
        return (
          !INVALID_FIELD_VALUES.includes(value) &&
          !(Array?.isArray(value) && value?.length === 0)
        );
      });

      return hasData;
    });

    if (setEditingKeys) {
      setEditingKeys((prev) => {
        const newKeys = new Set(prev);
        currentData?.forEach((row) => {
          if (row?.isEmptyRow) {
            const hasData = tableHeaders?.some((item) => {
              const value = row[item?.dataIndex];
              return (
                !INVALID_FIELD_VALUES.includes(value) &&
                !(Array?.isArray(value) && value?.length === 0)
              );
            });

            if (!hasData && !row?.isNew) {
              newKeys?.delete(row?.key);
            }
          }
        });
        return newKeys;
      });
    }

    const hasEmptyRow = dataWithoutEmptyRows?.some((row) => {
      if (!row?.isEmptyRow) return false;

      const isEmpty = !tableHeaders?.some((item) => {
        const value = row[item?.dataIndex];
        return (
          !INVALID_FIELD_VALUES.includes(value) &&
          !(Array?.isArray(value) && value?.length === 0)
        );
      });

      return isEmpty;
    });

    if (!hasEmptyRow) {
      const newEmptyRow = tableHeaders?.reduce(
        (acc, field) => {
          acc[field?.dataIndex] = undefined;
          return acc;
        },
        {
          key: `new-${Date?.now()}`,
          isEmptyRow: true,
        },
      );
      dataWithoutEmptyRows?.push(newEmptyRow);

      if (setEditingKeys) {
        setEditingKeys((prev) => new Set([...prev, newEmptyRow?.key]));
      }
    }
    handleFieldsDataRefUpdate(dataWithoutEmptyRows);
    setTableData(dataWithoutEmptyRows);
    return dataWithoutEmptyRows?.length - 1;
  };

  const [createRegisterLogFormFieldEntry] = useMutation(
    CREATE_REGISTER_TABLE_FIELDS,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted() {
        setIsLoading(false);
        handleTableRouterPromptUpdate(false);
        showRouterPrompt(false);
        setIsAddMoreDisabled(true);
        // Refetch register data and register details after successful creation
        if (handleRegisterRefetch) {
          handleRegisterRefetch();
        }
        if (handleRegisterLogDetailRefetch) {
          handleRegisterLogDetailRefetch();
        }
        if (handleHisotryRefetch) {
          handleHisotryRefetch();
        }
      },
    },
  );

  const [updateRegisterLogFormFieldEntry] = useMutation(
    UPDATE_REGISTER_LOG_DETAIL_ENTRY,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted() {
        setIsLoading(false);
        handleTableRouterPromptUpdate(false);
        showRouterPrompt(false);
        setIsAddMoreDisabled(true);
        // Refetch register data and register details after successful update
        if (handleRegisterRefetch) {
          handleRegisterRefetch();
        }
        if (handleRegisterLogDetailRefetch) {
          handleRegisterLogDetailRefetch();
        }
        if (handleHisotryRefetch) {
          handleHisotryRefetch();
        }
      },
    },
  );

  const [fetchSignedUrl] = useLazyQuery(GET_URLS, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (showModal) {
      const attachmentFields = tableHeaders?.filter(
        (field) => field?.type === FIELD_TYPES.ATTACHMENT,
      );
      if (attachmentFields?.length > 0) {
        const newFileLists = [];
        const newInitialKeys = {};

        attachmentFields?.forEach((attachmentField) => {
          const fieldName = `${fieldIndex}_${attachmentField?.dataIndex}`;
          const existingAttachments = rowRecord[attachmentField?.dataIndex];
          if (Array.isArray(existingAttachments)) {
            const fieldInitialFiles = existingAttachments?.map((item, idx) => {
              if (item?.originFileObj || (item?.originFileObj && item?.key)) {
                return item;
              }
              const isPdf =
                item?.key?.split('.')?.[item?.key?.split('.')?.length - 1] ===
                  'pdf' || item?.type === 'application/pdf';
              return {
                uid: `${fieldName}-${idx}`,
                status: 'done',
                url: item?.value || item?.url || item?.thumbUrl,
                key: item?.key,
                name:
                  item?.name ||
                  (item?.key ? item?.key?.split('/')?.pop() : undefined),
                type: item?.type,
                blobUrl: item?.blobUrl,
                thumbUrl: isPdf
                  ? FALLBACK_PDF_URL
                  : item?.value || item?.url || item?.thumbUrl,
              };
            });
            const fieldKeys = fieldInitialFiles
              ?.map((file) => file?.key)
              ?.filter(Boolean);

            newInitialKeys[fieldName] = fieldKeys;
            newFileLists[fieldName] = fieldInitialFiles;
            fileLists.current[fieldName] = fieldInitialFiles;
            tempUploadDataRef.current = {
              ...(tempUploadDataRef?.current || {}),
              [fieldName]: existingAttachments,
            };

            form.setFieldsValue({
              [fieldName]: fieldInitialFiles,
            });
          }
        });
        setInitialKeys(newInitialKeys);
        fileLists.current = {
          ...fileLists?.current,
          ...newFileLists,
        };
      }
    }
  }, [showModal, tableHeaders, fieldIndex, form]);

  useEffect(() => {
    if (showModal && isFieldClicked && isFocusedFieldRef?.current) {
      clearTimeout();
      setTimeout(() => {
        isFocusedFieldRef?.current?.focus();
      }, 500);
    }
  }, [showModal, isFieldClicked]);

  const handleUploadImage = async (newFileList, fieldName) => {
    try {
      if (!newFileList?.length) return;

      const limitedFileList = newFileList?.slice(-3);
      const filteredFileList = filter(
        limitedFileList,
        (file) => file?.originFileObj?.name,
      );

      if (!filteredFileList?.length) return;

      const fileSend = (anotherFileList) => {
        return map(anotherFileList, (file) => {
          const fileType = file?.originFileObj?.type || file?.type;
          const fileName = file?.originFileObj?.name || file?.name;
          return {
            fileName: `register/attachments/${fileName}`,
            contentType: fileType,
          };
        });
      };
      if (filteredFileList?.length) {
        try {
          const { data } = await fetchSignedUrl({
            variables: {
              data: fileSend(filteredFileList),
            },
          });

          const keys = data?.getSignedPutUrls?.keys || [];

          const objectFiles = tempUploadDataRef?.current?.[fieldName]?.filter(
            (item) => item?.originFileObj,
          );
          const promises = map(
            data?.getSignedPutUrls?.signedUrls,
            async (imageUrl, index) => {
              const callBack = async (image) => {
                const watermarkedImageUrl = await addWatermark(image);
                const file = dataURLtoFile(
                  watermarkedImageUrl,
                  objectFiles?.[index]?.originFileObj?.name,
                );
                return fileUpload(imageUrl, file)
                  ?.then(() => {
                    return keys[index];
                  })
                  ?.catch(() => {
                    return message.error('Image upload failed!');
                  });
              };
              if (
                includes(
                  [...ALLOWED_IMAGE_TYPES],
                  objectFiles?.[index]?.originFileObj?.type,
                )
              ) {
                return compressImage({
                  file: objectFiles?.[index]?.originFileObj,
                  fileType: objectFiles?.[index]?.type?.split('/')?.[1],
                  callBack,
                });
              }
              return fileUpload(imageUrl, objectFiles[index]?.originFileObj)
                ?.then(() => {
                  return keys[index];
                })
                ?.catch(() => {
                  return message.error('File upload failed!');
                });
            },
          );
          handleTableRouterPromptUpdate(true);
          showRouterPrompt(true);
          const uploadedKeys = await Promise.all(promises);
          const validKeys = uploadedKeys?.filter((key) => key);

          if (fileLists?.current[fieldName] && validKeys?.length > 0) {
            const updatedFileList = [...(fileLists?.current[fieldName] || [])];
            filteredFileList?.forEach((file, i) => {
              if (validKeys[i]) {
                const fileIndex = updatedFileList?.findIndex(
                  (f) => f?.uid === file?.uid,
                );
                if (fileIndex >= 0) {
                  updatedFileList[fileIndex] = {
                    ...updatedFileList[fileIndex],
                    key: validKeys[i],
                  };
                }
              }
            });

            if (Object?.keys(initialKeys)?.length > 0) {
              imageKeys.current[fieldName] = [
                ...(initialKeys?.[fieldName] || []),
                ...validKeys,
              ];
            } else {
              imageKeys.current[fieldName] = [...validKeys];
            }
            fileLists.current = {
              ...fileLists?.current,
              [fieldName]: updatedFileList,
            };
          }
          return validKeys;
        } catch (error) {
          return error;
        }
      }
    } catch (error) {
      return error;
    }
  };

  const handleFieldChange = (changedFields) => {
    const [fieldName, value] = Object?.entries(changedFields)?.[0];
    const [rowIndex, fieldType] = fieldName?.split('_');
    const changedField = tableHeaders?.find(
      (field) => field?.dataIndex === fieldType,
    );
    let formattedValue = value;
    if (changedField?.type === FIELD_TYPES.NUMERIC) {
      formattedValue = formatNumericInput(value);
    }
    form?.setFieldsValue({
      [fieldName]: formattedValue,
    });

    if (
      includes(
        [FIELD_TYPES.NUMERIC, FIELD_TYPES.DATE, FIELD_TYPES.TIME],
        changedField?.type,
      )
    ) {
      const calculationFields = tableHeaders
        ?.filter((field) => field?.type === FIELD_TYPES.CALCULATION)
        ?.map((field) => field?.dataIndex);

      if (calculationFields?.length) {
        calculationFields?.forEach((calcField) => {
          const calcFieldName = `${rowIndex}_${calcField}`;
          const calculatedValue = calculateFieldValue(
            calcFieldName,
            form,
            tableHeaders,
          );
          form?.setFieldsValue({
            [calcFieldName]: calculatedValue,
          });
        });
      }
    }
  };

  const renderFields = () => {
    return map(tableHeaders, (item) => {
      const fieldName = `${fieldIndex}_${item?.dataIndex}`;
      switch (item?.type) {
        case FIELD_TYPES.NUMERIC:
          return (
            <Form.Item
              name={fieldName}
              label={
                <span className="d-flex align-center">
                  <div className="mr-5">
                    <FieldIcon fieldType={item?.type} />
                  </div>
                  {item?.name}
                </span>
              }
              initialValue={rowRecord[item?.dataIndex]}
              rules={[
                {
                  required: item?.isRequired,
                  message: 'This field is required!',
                },
                {
                  max: 15,
                  message: 'Maximum 15 digits are allowed',
                },
                {
                  validator: (_, value) => {
                    if (!value || value === '') {
                      return Promise?.resolve();
                    }
                    const numericValue = Number(value);
                    if (Number?.isNaN(numericValue)) {
                      return Promise?.reject(
                        new Error('Please enter a valid number'),
                      );
                    }
                    return Promise?.resolve();
                  },
                },
              ]}
              normalize={(value) => formatNumericInput(value)}
            >
              <Input
                id={`table-field-numeric-input-${fieldName}`}
                ref={isFieldClicked === fieldName ? isFocusedFieldRef : null}
                placeholder="Enter the number"
                inputMode="decimal"
                disabled={isAuditor}
                onChange={(e) => {
                  const value = e?.target?.value;
                  handleFieldChange({ [fieldName]: value });
                }}
                allowClear
              />
            </Form.Item>
          );

        default:
          return (
            <RenderFields
              fieldType={item?.type}
              fieldName={item?.name}
              fieldIndex={fieldName}
              fieldIsRequired={item?.isRequired}
              initialFieldValue={rowRecord[item?.dataIndex]}
              fieldDisabled={isAuditor}
              inputRef={isFieldClicked === fieldName ? isFocusedFieldRef : null}
              multiLineRef={
                isFieldClicked === fieldName ? isFocusedFieldRef : null
              }
              dateRef={isFieldClicked === fieldName ? isFocusedFieldRef : null}
              dateOpen={isDateFocused === fieldName}
              onDateFocused={() => setIsDateFocused(fieldName)}
              onDateOpenChange={(e) => {
                if (!e) {
                  setIsDateFocused();
                }
              }}
              onDateChange={(date) => {
                handleFieldChange({ [fieldName]: date });
              }}
              timeRef={isFieldClicked === fieldName ? isFocusedFieldRef : null}
              timeOpen={isTimeFocused === fieldName}
              onTimeFocused={() => setIsTimeFocused(fieldName)}
              onTimeOpenChange={(e) => {
                if (!e) {
                  setIsTimeFocused();
                }
              }}
              onTimeChange={(time) => {
                handleFieldChange({ [fieldName]: time });
              }}
              optionsRef={
                isFieldClicked === fieldName ? isFocusedFieldRef : null
              }
              optionsOpen={isOptionsFocused === fieldName}
              onOptionsFocus={() => setIsOptionsFocused(fieldName)}
              onOptionChange={() => setIsOptionsFocused(null)}
              onOptionBlur={() => setIsOptionsFocused(null)}
              onOptionOpenChange={(open) => {
                if (!open) {
                  setIsOptionsFocused(null);
                } else {
                  setIsOptionsFocused(fieldName);
                }
              }}
              options={map(item?.options, (option) => ({
                label: option,
                value: option,
              }))}
              multiSelectOpen={isMultiSelectOptionsFocused === fieldName}
              onMultiSelectFocus={() =>
                setIsMultiSelectOptionsFocused(fieldName)
              }
              onMultiSelectOpenChange={(open) => {
                if (!open) {
                  setIsMultiSelectOptionsFocused(null);
                } else {
                  setIsMultiSelectOptionsFocused(fieldName);
                }
              }}
              onMultiSelectBlur={() => setIsMultiSelectOptionsFocused(null)}
              multiSelectRef={
                isFieldClicked === fieldName ? isFocusedFieldRef : null
              }
              uploadKey={`${fieldIndex}-${item?.dataIndex}`}
              openFileDialogOnClick
              fileListRef={fileLists}
              records={rowRecord}
              uploadDataIndex={item?.dataIndex}
              uploadIndex={fieldIndex}
              uploadFieldName={fieldName}
              handleUploadChange={(info) => {
                if (!isFormChanged) {
                  setIsFormChanged(true);
                }
                const newFileList = filter(info?.fileList, (file) => {
                  const fileType = file?.originFileObj?.type || file?.type;
                  if (
                    !file?.url &&
                    fileType &&
                    !includes(
                      [...ALLOWED_IMAGE_TYPES, 'application/pdf'],
                      fileType,
                    )
                  ) {
                    message.destroy();
                    message.error('File type should be PNG, JPG, JPEG, PDF');
                    return false;
                  }

                  return true;
                });

                fileLists.current = {
                  ...fileLists?.current,
                  [fieldName]: newFileList,
                };
                tempUploadDataRef.current = {
                  ...(tempUploadDataRef?.current || {}),
                  [fieldName]: newFileList,
                };

                form.setFieldsValue({
                  [fieldName]: newFileList,
                });

                const values = form?.getFieldsValue();
                const requiredFields = tableHeaders?.filter(
                  (field) => field?.isRequired,
                );
                if (requiredFields?.length > 0) {
                  const filteredData = requiredFields?.filter((field) => {
                    const value = values[`${fieldIndex}_${field?.dataIndex}`];
                    return value;
                  });
                  setIsAddMoreDisabled(
                    !(filteredData?.length === requiredFields?.length),
                  );
                } else {
                  setIsAddMoreDisabled(false);
                }
              }}
              beforeUpload={(file) => {
                const currentFiles = fileLists?.current[fieldName] || [];
                if (currentFiles?.length >= 3) {
                  message.destroy();
                  message.warning('Maximum 3 files are allowed');
                  return Upload.LIST_IGNORE;
                }
                if (file?.size > FILE_SIZE_8MB) {
                  message.destroy();
                  message.error('Maximum file size is 8MB');
                  return Upload.LIST_IGNORE;
                }
                const newFile = file;
                if (file?.type?.startsWith('image/')) {
                  newFile.thumbUrl = URL?.createObjectURL(file);
                } else if (file?.type === 'application/pdf') {
                  newFile.thumbUrl = FALLBACK_PDF_URL;
                }
                return false;
              }}
              onUploadRemove={(file) => {
                const currentFileList = [
                  ...(fileLists?.current[fieldName] || []),
                ];
                const updatedFileList = currentFileList?.filter(
                  (f) => f?.uid !== file?.uid,
                );
                fileLists.current = {
                  ...fileLists?.current,
                  [fieldName]: updatedFileList,
                };
                const updatedKeys = initialKeys[fieldName]?.filter(
                  (key) => key !== file?.key,
                );
                setInitialKeys((prev) => ({
                  ...prev,
                  [fieldName]: updatedKeys,
                }));
              }}
              onCalculationChange={(e) =>
                handleFieldChange({ [fieldName]: e?.target?.value })
              }
            />
          );
      }
    });
  };

  const handleCancel = () => {
    if (isFormChanged === true) {
      setShowTableFieldConfirmModal(true);
      handleTableRouterPromptUpdate(true);
      showRouterPrompt(true);
    } else {
      if (rowRecord?.isNew || rowRecord?.isUpdate) {
        if (setEditingKeys && rowRecord?.key) {
          setEditingKeys((prev) => new Set([...prev, rowRecord?.key]));
        }
        handleTableRouterPromptUpdate(true);
        showRouterPrompt(true);
      } else {
        handleTableRouterPromptUpdate(false);
        showRouterPrompt(false);
      }
      setShowModal(false);
      form?.resetFields();
    }
  };

  const onFormSubmit = async () => {
    if (isLoading) return;
    setIsLoading(true);
    const formValues = form?.getFieldsValue();
    const responses = [];
    const hasError = false;
    const formattedFiles = [];

    await Promise.all(
      tableHeaders?.map(async (column) => {
        const fieldName = `${fieldIndex}_${column?.dataIndex}`;
        let fieldValue = formValues[fieldName];

        if (column?.type === FIELD_TYPES.DATE) {
          if (fieldValue) {
            if (moment(fieldValue)?.isValid()) {
              fieldValue = moment(fieldValue)?.format(DEFAULTDATEFORMAT);
            }
          }
        }

        if (column?.type === FIELD_TYPES.TIME && fieldValue) {
          if (moment(fieldValue)?.isValid()) {
            fieldValue = moment(new Date(fieldValue))?.format(
              TIME_24_HOUR_FORMAT,
            );
          }
        }

        if (column?.type === FIELD_TYPES.ATTACHMENT) {
          const currentFileList = fileLists?.current[fieldName] || [];
          let validKeys = [];
          const hasOriginFileObj = currentFileList?.some(
            (file) => file?.originFileObj !== undefined,
          );
          if (currentFileList?.length > 0 && hasOriginFileObj) {
            validKeys =
              (await handleUploadImage(currentFileList, fieldName)) || [];
          }
          const existingKeys = initialKeys[fieldName] || [];
          const finalKeys = [...existingKeys, ...validKeys];

          fieldValue = finalKeys?.join(',');
          formattedFiles[fieldName] = currentFileList?.map((item, idx) => {
            const isPdf =
              (validKeys?.[idx] || item?.key || '')?.endsWith('.pdf') ||
              item?.type === 'application/pdf';
            let blobUrl = null;

            if (isPdf && item?.originFileObj) {
              blobUrl = URL?.createObjectURL(item?.originFileObj);
            }

            return {
              uid: `${fieldName}-${idx}`,
              status: 'done',
              url: item?.value || item?.thumbUrl,
              key: item?.key || finalKeys?.[idx],
              name: item?.name,
              type: item?.type,
              blobUrl: blobUrl || item?.blobUrl,
              thumbUrl: isPdf ? FALLBACK_PDF_URL : item?.thumbUrl,
            };
          });
          handleFileListRefUpdate([...formattedFiles[fieldName]], fieldName);
        }

        if (column?.type === FIELD_TYPES.CHECKBOXES) {
          if (fieldValue) {
            fieldValue = fieldValue?.join(',');
          }
        }
        responses?.push({
          registerFormFieldId: column?.id,
          value: trim(fieldValue) || '',
        });
      }),
    );

    if (hasError) {
      return;
    }

    try {
      if (rowRecord?.id) {
        await updateRegisterLogFormFieldEntry({
          variables: {
            updateRegisterLogFormFieldEntryId: rowRecord?.id,
            data: {
              isNewRow: false,
              responses,
            },
          },
        });
        const obj = {};
        const tableFieldValues = tableForm?.getFieldsValue();
        Object?.entries(formValues)?.forEach(([key, value]) => {
          obj[key?.split('_')?.[1]] = trim(value);
          obj.id = rowRecord?.id;
          if (key in formValues) {
            tableFieldValues[key] = trim(value);
          }
        });
        tableForm?.setFieldsValue(tableFieldValues);
        const updatedFieldsData = fieldsData?.current?.map((field) =>
          field?.id === rowRecord?.id ? obj : field,
        );
        handleFieldsDataRefUpdate([...updatedFieldsData]);
        setTableData([...updatedFieldsData]);

        if (setEditingKeys && rowRecord?.key) {
          setEditingKeys((prev) => {
            const newKeys = new Set(prev);
            newKeys.delete(rowRecord.key);
            return newKeys;
          });
        }

        const newEmptyRowIndex = ensureEmptyRowExists();
        setFieldIndex(newEmptyRowIndex);
        Object?.entries(formattedFiles)?.forEach(([key, files]) => {
          const [indexStr, fieldName] = key?.split('_');
          const index = parseInt(indexStr, 10);
          handleFieldsDataRefUpdate(files, index, fieldName);
        });
      } else {
        const result = await createRegisterLogFormFieldEntry({
          variables: {
            registerLogId,
            data: {
              filling: null,
              tableEntries: responses?.filter(Boolean),
            },
          },
        });
        if (result?.data?.createRegisterLogFormFieldEntry?.id) {
          const obj = {};
          const newId = result?.data?.createRegisterLogFormFieldEntry?.id;
          Object?.entries(formValues)?.forEach(([key, value]) => {
            obj[key?.split('_')?.[1]] = value;
            obj.id = newId;
            obj.key = newId;
            obj.isEmptyRow = false;
          });
          if (rowRecord?.isUpdate) {
            fieldsData?.current?.splice(fieldIndex, 1, obj);
          } else {
            fieldsData?.current?.splice(fieldIndex, 0, obj);
          }
          Object?.assign(fileListRef?.current, fileLists?.current);
          Object?.entries(formattedFiles)?.forEach(([key, files]) => {
            const [indexStr, fieldName] = key?.split('_');
            const index = parseInt(indexStr, 10);
            handleFieldsDataRefUpdate(files, index, fieldName);
          });
          setTableData([...fieldsData?.current]);
          const updatedFormValues = tableForm?.getFieldsValue();
          Object?.entries(formValues)?.forEach(([key, value]) => {
            updatedFormValues[key] = value;
          });
          tableForm?.setFieldsValue(updatedFormValues);

          if (setEditingKeys) {
            setEditingKeys((prev) => {
              const newKeys = new Set(prev);
              if (rowRecord?.key) {
                newKeys.delete(rowRecord.key);
              }
              return newKeys;
            });
          }
        }
        const newEmptyRowIndex = ensureEmptyRowExists();
        setFieldIndex(newEmptyRowIndex);
      }
      if (!saveClickedRef?.current) {
        addMoreButtonRef?.current?.blur();
        const modalBody =
          // eslint-disable-next-line no-undef
          document?.getElementById('register-mobile-responsive-content-body');
        if (modalBody) {
          modalBody?.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setRowRecord({});
        setIsFormChanged(false);
      } else {
        setShowModal(false);
      }
    } catch (error) {
      message.error('Failed to save form data');
    } finally {
      saveClickedRef.current = false;
    }
  };

  const syncFormDataToRefs = () => {
    const formValues = form?.getFieldsValue();
    const tableFieldValues = tableForm?.getFieldsValue();
    const dataObject = {};
    Object?.entries(formValues)?.forEach(([key, value]) => {
      const fieldKey = key?.split('_')?.[1];
      dataObject[fieldKey] = value;
      if (key in formValues) {
        fileLists.current[key] = formValues?.[key];
        handleFileListRefUpdate(formValues?.[key], key);
        tableFieldValues[key] = value;
        if (modifiedFieldsRef?.current) {
          modifiedFieldsRef?.current?.add(key);
        }
      }
    });
    tableForm?.setFieldsValue(tableFieldValues);
    return dataObject;
  };

  const handleSaveClick = async () => {
    const formValues = form?.getFieldsValue();
    let allRequiredFieldsFilled = true;
    tableHeaders?.forEach((field) => {
      const fieldName = `${fieldIndex}_${field?.dataIndex}`;
      if (
        field?.isRequired &&
        (!formValues[fieldName] ||
          (Array?.isArray(formValues[fieldName]) &&
            formValues[fieldName]?.length === 0))
      ) {
        allRequiredFieldsFilled = false;
      }
    });

    if (allRequiredFieldsFilled) {
      saveClickedRef.current = true;
      await onFormSubmit();
    } else {
      if (onTableDirty) {
        onTableDirty();
      }
      const obj = syncFormDataToRefs();
      if (rowRecord?.id) {
        obj.id = rowRecord?.id;
        const updatedFieldsData = fieldsData?.current?.map((field) =>
          field?.id === rowRecord?.id ? obj : field,
        );
        handleFieldsDataRefUpdate([...updatedFieldsData]);
        setTableData([...updatedFieldsData]);
      } else {
        if (rowRecord?.isNew || rowRecord?.isEmptyRow) {
          obj.isEmptyRow = false;
          obj.key = rowRecord?.key || `temp_${Date.now()}`;
          obj.isUpdate = true;
          obj.isNew = true;

          fieldsData?.current?.splice(fieldIndex, 1, obj);

          if (setEditingKeys) {
            setEditingKeys((prev) => new Set([...prev, obj?.key]));
          }
        } else {
          obj.isEmptyRow = false;
          obj.key = `temp_${Date?.now()}`;
          obj.isUpdate = true;
          obj.isNew = true;
          fieldsData?.current?.splice(fieldIndex, 0, obj);

          if (setEditingKeys) {
            setEditingKeys((prev) => new Set([...prev, obj?.key]));
          }
        }

        setTableData([...fieldsData?.current]);

        const newEmptyRowIndex = ensureEmptyRowExists();
        setFieldIndex(newEmptyRowIndex);
      }
      if (onTableErrorCheck) {
        onTableErrorCheck(true);
      }
      handleTableRouterPromptUpdate(true);
      showRouterPrompt(true);
      message.destroy();
      message.warning(
        'Data saved locally, but some required fields are missing',
      );
      setShowModal(false);
    }
  };

  const handlePromptSave = async (saveDraftPayload) => {
    const currentFormValues = form?.getFieldsValue();
    const updatedRowData = {};
    Object?.keys(currentFormValues)?.forEach((formKey) => {
      const [rowIndexString, fieldName] = formKey?.split('_');
      const currentRowIndex = parseInt(rowIndexString, 10);
      if (currentRowIndex === fieldIndex) {
        let currentFieldValue = currentFormValues?.[formKey];
        if (currentFieldValue && moment?.isMoment(currentFieldValue)) {
          const columnConfig = tableHeaders?.find(
            (header) => header?.dataIndex === fieldName,
          );
          if (columnConfig?.type === FIELD_TYPES?.DATE) {
            currentFieldValue = currentFieldValue?.format(DEFAULTDATEFORMAT);
          } else if (columnConfig?.type === FIELD_TYPES?.TIME) {
            currentFieldValue = currentFieldValue?.format(TIME_24_HOUR_FORMAT);
          }
        }
        updatedRowData[fieldName] = currentFieldValue;
      }
    });
    if (fieldsData?.current?.[fieldIndex]) {
      const existingRowData = fieldsData?.current?.[fieldIndex];
      // eslint-disable-next-line no-param-reassign
      fieldsData.current[fieldIndex] = {
        ...existingRowData,
        ...updatedRowData,
      };
      if (handleFieldsDataRefUpdate) {
        handleFieldsDataRefUpdate([...fieldsData.current]);
      }
      if (setTableData) {
        setTableData([...fieldsData.current]);
      }
    }
    syncFormDataToRefs();
    if (handleSaveAsDraft) {
      await handleSaveAsDraft(saveDraftPayload);
    }
  };

  return (
    <div id="register-mobile-responsive-modal">
      <Modal
        maskClosable={false}
        centered
        form={form}
        open={showModal}
        closeIcon={<WrongIcon className="close-btn" />}
        className="add-edit-table-field-modal"
        onCancel={handleCancel}
        footer={null}
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById('register-mobile-responsive-modal')
        }
      >
        {
          //! giving inline style as need to generate image from this div
        }
        <div style={{ opacity: 0, height: 0 }}>
          <div
            id={ELEMENT_IDS.TIME_STAMP}
            style={{
              background: '#000',
              color: '#3366ff',
              width: 'max-content',
            }}
          >
            {`${moment()?.format(DATETIMEWITHDIVIDE)} - ${currentProjectDetails?.timeZone}`}
          </div>
        </div>
        <h2>{rowRecord?.id ? 'Edit' : 'Add'} Field</h2>
        <h3>Sr. No. {fieldIndex + 1}</h3>
        <Form
          layout="vertical"
          form={form}
          onFinish={onFormSubmit}
          onValuesChange={() => {
            if (!isFormChanged) {
              setIsFormChanged(true);
              handleTableRouterPromptUpdate(true);
              showRouterPrompt(true);
            }
            const values = form?.getFieldsValue();
            const requiredFields = tableHeaders?.filter(
              (item) => item?.isRequired,
            );

            if (requiredFields?.length > 0) {
              const filteredData = requiredFields?.filter((field) => {
                const value = values[`${fieldIndex}_${field?.dataIndex}`];
                return value;
              });
              setIsAddMoreDisabled(
                !(filteredData?.length === requiredFields?.length),
              );
            } else {
              setIsAddMoreDisabled(false);
            }
          }}
        >
          <div className="overflow-y-auto">{renderFields()}</div>
          <div className="form-buttons mt-10">
            <CanPerform
              action={ALLOWED_ACTION_KEYS.ADD_REGISTER_LOG}
              type={ALLOWED_ACTION_TYPE.BOTH}
            >
              <Button
                id="table-field-add-more-btn"
                ref={addMoreButtonRef}
                shape="round"
                type="primary"
                className="save-button"
                htmlType="submit"
                disabled={isLoading || isAddMoreDisabled}
              >
                Add More
              </Button>
            </CanPerform>
            <Button
              id="table-field-save-btn"
              shape="round"
              type="primary"
              className="cancel-button"
              disabled={!isFormChanged}
              onClick={handleSaveClick}
            >
              Save
            </Button>
          </div>
        </Form>
      </Modal>
      {showTableFieldConfirmModal && (
        <TableFieldUnsaveChangeModal
          showPrompt={showTableFieldConfirmModal}
          setShowPrompt={setShowTableFieldConfirmModal}
          onSaveDraft={handlePromptSave}
        />
      )}
    </div>
  );
};

export default AddEditTableFieldModal;
